import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Save } from 'lucide-react';

export interface ScheduleConfig {
  frequency: 'daily' | 'weekly' | 'monthly';
  time: string;
  dayOfWeek: number;
  dayOfMonth: number;
  timezone?: string;
}

interface ScheduleConfigFormProps {
  config: ScheduleConfig;
  onUpdate: (config: Partial<ScheduleConfig>) => void;
  isUpdating?: boolean;
}

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const DAYS_OF_MONTH = Array.from({ length: 31 }, (_, i) => i + 1);

export function ScheduleConfigForm({
  config: initialConfig,
  onUpdate,
  isUpdating = false,
}: ScheduleConfigFormProps) {
  const [config, setConfig] = useState<ScheduleConfig>(initialConfig);

  // Update local state when initialConfig changes
  useEffect(() => {
    setConfig(initialConfig);
  }, [initialConfig]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onUpdate(config);
  };

  const getNextReportPreview = (): string => {
    const now = new Date();
    const [hours, minutes] = config.time.split(':').map(Number);
    const next = new Date(now);
    next.setHours(hours, minutes, 0, 0);

    switch (config.frequency) {
      case 'daily':
        // If the time has already passed today, schedule for tomorrow
        if (next <= now) {
          next.setDate(next.getDate() + 1);
        }
        break;

      case 'weekly':
        // Find next occurrence of the selected day of week
        const targetDay = config.dayOfWeek;
        const currentDay = now.getDay();
        let daysUntilTarget = targetDay - currentDay;

        // If the day has passed this week, or it's today but time has passed, go to next week
        if (daysUntilTarget < 0 || (daysUntilTarget === 0 && next <= now)) {
          daysUntilTarget += 7;
        }

        next.setDate(next.getDate() + daysUntilTarget);
        break;

      case 'monthly':
        // Find next occurrence of the selected day of month
        next.setDate(config.dayOfMonth);

        // If the date has passed this month, go to next month
        if (next <= now) {
          next.setMonth(next.getMonth() + 1);
          // After incrementing month, re-apply the day
          next.setDate(config.dayOfMonth);
        }

        // Handle months with fewer days (e.g., Feb 30th -> last day of Feb)
        // When setting an invalid date like Feb 30, JS auto-rolls to next month
        // We need to go back to the last valid day of the intended month
        if (next.getDate() !== config.dayOfMonth) {
          // Set to day 0 of the NEXT month = last day of current month
          next.setMonth(next.getMonth() + 1);
          next.setDate(0);
        }
        break;
    }

    return next.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: config.timezone || 'UTC',
      timeZoneName: 'short',
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        <h3 className="text-lg font-bold text-slate-900 mb-6">Report Schedule</h3>

        <div className="space-y-6">
          {/* Frequency Selector */}
          <div>
            <Label htmlFor="frequency" className="text-sm font-semibold text-slate-900 mb-2 block">
              Frequency
            </Label>
            <Select
              value={config.frequency}
              onValueChange={(value) =>
                setConfig((prev) => ({
                  ...prev,
                  frequency: value as ScheduleConfig['frequency'],
                }))
              }
            >
              <SelectTrigger
                id="frequency"
                className="w-full rounded-xl border-slate-200 h-12"
              >
                <SelectValue placeholder="Select frequency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Time Picker */}
          <div>
            <Label htmlFor="time" className="text-sm font-semibold text-slate-900 mb-2 block">
              Time
            </Label>
            <Input
              id="time"
              type="time"
              value={config.time}
              onChange={(e) =>
                setConfig((prev) => ({ ...prev, time: e.target.value }))
              }
              className="w-full rounded-xl border-slate-200 h-12"
              required
            />
          </div>

          {/* Day of Week Selector - shown only for weekly */}
          {config.frequency === 'weekly' && (
            <div>
              <Label htmlFor="dayOfWeek" className="text-sm font-semibold text-slate-900 mb-2 block">
                Day of Week
              </Label>
              <Select
                value={config.dayOfWeek.toString()}
                onValueChange={(value) =>
                  setConfig((prev) => ({
                    ...prev,
                    dayOfWeek: parseInt(value, 10),
                  }))
                }
              >
                <SelectTrigger
                  id="dayOfWeek"
                  className="w-full rounded-xl border-slate-200 h-12"
                >
                  <SelectValue placeholder="Select day" />
                </SelectTrigger>
                <SelectContent>
                  {DAYS_OF_WEEK.map((day) => (
                    <SelectItem key={day.value} value={day.value.toString()}>
                      {day.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Day of Month Selector - shown only for monthly */}
          {config.frequency === 'monthly' && (
            <div>
              <Label htmlFor="dayOfMonth" className="text-sm font-semibold text-slate-900 mb-2 block">
                Day of Month
              </Label>
              <Select
                value={config.dayOfMonth.toString()}
                onValueChange={(value) =>
                  setConfig((prev) => ({
                    ...prev,
                    dayOfMonth: parseInt(value, 10),
                  }))
                }
              >
                <SelectTrigger
                  id="dayOfMonth"
                  className="w-full rounded-xl border-slate-200 h-12"
                >
                  <SelectValue placeholder="Select day" />
                </SelectTrigger>
                <SelectContent>
                  {DAYS_OF_MONTH.map((day) => (
                    <SelectItem key={day} value={day.toString()}>
                      {day}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Next Report Preview */}
          <div className="pt-4 border-t border-slate-200">
            <p className="text-sm text-slate-600 mb-2 font-semibold">Next report scheduled for:</p>
            <p className="text-sm text-slate-900 bg-slate-50 p-3 rounded-xl border border-slate-200">
              {getNextReportPreview()}
            </p>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          type="submit"
          disabled={isUpdating}
          className="rounded-xl bg-[#4669ed] hover:bg-[#3654c9] text-white font-bold px-6 h-12"
        >
          {isUpdating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save Schedule
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
